import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function RulesPage() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["rules"],
    queryFn: () => api.get("/rules").then((r) => r.data),
  });

  const filtered = useMemo(() => {
    if (!data?.categories) return [];
    if (!q.trim()) return data.categories;
    const needle = q.toLowerCase();
    return data.categories
      .map((c) => ({ ...c, rules: c.rules.filter((r) => r.toLowerCase().includes(needle) || c.title.toLowerCase().includes(needle)) }))
      .filter((c) => c.rules.length > 0);
  }, [data, q]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <header className="mb-8">
        <Badge variant="outline" className="border-primary/40 text-primary font-mono-tech uppercase tracking-widest mb-3">
          Officiel
        </Badge>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">Règlement du serveur</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Le respect de ces règles est obligatoire pour tous les joueurs. En cas de doute, contactez le staff via le forum.
        </p>
        {data?.updated_at && (
          <p className="text-xs text-muted-foreground font-mono-tech mt-2">
            Dernière mise à jour : {new Date(data.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
          </p>
        )}
      </header>

      <div className="mb-8 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher dans le règlement..."
          className="pl-9"
          data-testid="rules-search-input"
        />
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Sommaire */}
        <aside className="lg:col-span-3">
          <div className="sticky top-24">
            <p className="font-display uppercase text-xs tracking-widest text-muted-foreground mb-3">Sommaire</p>
            <nav className="space-y-1">
              {(data?.categories || []).map((c) => (
                <a key={c.id} href={`#${c.id}`} className="block text-sm text-muted-foreground hover:text-primary py-1" data-testid={`rules-toc-${c.id}`}>
                  {c.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <div className="lg:col-span-9 space-y-6">
          {isLoading && <p className="text-muted-foreground">Chargement...</p>}
          {filtered.length === 0 && !isLoading && (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Aucune règle ne correspond à votre recherche.</CardContent></Card>
          )}
          {filtered.map((c) => (
            <Card key={c.id} id={c.id} className={c.important ? "border-primary/50" : ""} data-testid={`rules-cat-${c.id}`}>
              <CardContent className="p-6">
                <h2 className="font-display text-2xl uppercase mb-4 flex items-center gap-2">
                  {c.important && <AlertTriangle className="h-5 w-5 text-primary" />}
                  {c.title}
                </h2>
                <ol className="space-y-2 list-decimal list-inside marker:text-primary marker:font-bold">
                  {c.rules.map((r, i) => (
                    <li key={i} className="text-sm leading-relaxed">{r}</li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
